import React, {useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {toast} from "react-toastify";
import {faIcons} from "../../../utils/iconConstants";
import {toastError, toastInfo, toastSuccess} from "../../../utils/toast";
import {getUserEmail} from "../../../auth/accessTokenValidator";
import {
    currentCanonicalStoreId,
    linkageErrorMessage,
    removeLinkage,
    runUnlink,
    saveLinkage,
    UNLINK
} from "./storeLinkage";
import cleanupLinkageWithRetry from "./cleanupLinkageWithRetry";

const UnlinkSourceStoreModal = ({version, canonicalStoreId, member, onClose, onUnlinked}) => {
    const sourceStoreId = member.id;
    const [notes, setNotes] = useState('');
    const [step, setStep] = useState(null);
    const [savedUnlink, setSavedUnlink] = useState(undefined);
    const [cancelling, setCancelling] = useState(false);
    const submitting = step !== null;

    const onSubmit = () => {
        setStep('Saving');
        return saveLinkage(UNLINK, {version, canonicalStoreId, sourceStoreId, notes, user: getUserEmail(), index: savedUnlink?.index})
            .then(saved => {
                setSavedUnlink(saved);
                setStep('Unlinking');
                return runUnlink({canonicalStoreId, sourceStoreId}).then(result => {
                    setStep('Cleaning up');
                    return cleanupLinkageWithRetry(UNLINK, {version, sourceStoreId, canonicalStoreId, keepIndex: saved.index})
                        .then(() => result);
                });
            })
            .then(result => {
                if (result?.alreadyApplied) {
                    toastInfo(toast, `${sourceStoreId} was already unlinked from ${canonicalStoreId}`);
                } else {
                    toastSuccess(toast, `Unlinked ${sourceStoreId} from ${canonicalStoreId}`);
                }
                onUnlinked();
            })
            .catch(unlinkError => {
                toastError(toast, `Failed to unlink ${sourceStoreId} from ${canonicalStoreId}: ${linkageErrorMessage(unlinkError)}`);
                setStep(null);
            });
    };

    const removeUnlinkAndClose = () => {
        setStep('Removing');
        return removeLinkage(UNLINK, {version, record: savedUnlink}).then(() => onClose());
    };

    const keepUnlinkAndCleanUp = () => {
        toastInfo(toast, `${sourceStoreId} is no longer linked to ${canonicalStoreId}, so the unlink record was kept`);
        setStep('Cleaning up');
        return cleanupLinkageWithRetry(UNLINK, {version, sourceStoreId, canonicalStoreId, keepIndex: savedUnlink.index})
            .then(() => onUnlinked());
    };

    const checkGraphThenCancel = () => {
        setCancelling(true);
        setStep('Checking');
        return currentCanonicalStoreId({version, source: member.source, sourceStoreId: member.sourceStoreId})
            .then(current => current === canonicalStoreId ? removeUnlinkAndClose() : keepUnlinkAndCleanUp());
    };

    const onCancel = () => {
        if (!savedUnlink) {
            onClose();
            return;
        }
        return checkGraphThenCancel().catch(cancelError => {
            toastError(toast, `Failed to cancel the unlink of ${sourceStoreId}: ${linkageErrorMessage(cancelError)}`);
            setCancelling(false);
            setStep(null);
        });
    };

    const stepInProgress = <><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/><span>{step}</span></>;

    return (
        <Modal
            isOpen={true}
            className="Modal unlink-source-store-modal"
            overlayClassName="Overlay"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={() => !submitting && onCancel()}
            testId={"unlink-source-store-modal"}
        >
            <h5 className={"modal-header-text"}>Unlink Source Store from Canonical Store</h5>
            <div className={"store-info-field"}>
                <div className={"store-info-field-label"}>Canonical Store</div>
                <div className={"store-info-field-value"} data-testid={"unlink-canonical-store-id"}>{canonicalStoreId}</div>
            </div>
            <div className={"store-info-field"}>
                <div className={"store-info-field-label"}>Source Store</div>
                <div className={"store-info-field-value"} data-testid={"unlink-source-store-id"}>{sourceStoreId}</div>
            </div>
            <div className={"store-info-field"}>
                <div className={"store-info-field-label"}>Notes</div>
                <textarea
                    title={'Unlink Notes'}
                    placeholder={'What reason do you want to unlink?'}
                    className={"modal-alerts-text-area-wide"}
                    value={notes}
                    disabled={submitting}
                    onChange={e => setNotes(e.target.value)}
                />
            </div>
            <div className={"right-things"}>
                <button type="button" className="button modal-button cancel" name="cancel"
                        onClick={onCancel} disabled={submitting}>
                    {cancelling ? stepInProgress : "Cancel"}
                </button>
                <button type="button" className={"button modal-button confirm"} name="submit"
                        onClick={onSubmit} disabled={submitting || !notes.trim()}>
                    {submitting && !cancelling ? stepInProgress : "Submit"}
                </button>
            </div>
        </Modal>
    );
};

UnlinkSourceStoreModal.propTypes = {
    version: PropTypes.string.isRequired,
    canonicalStoreId: PropTypes.string.isRequired,
    member: PropTypes.shape({
        id: PropTypes.string.isRequired,
        source: PropTypes.string.isRequired,
        sourceStoreId: PropTypes.string.isRequired,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onUnlinked: PropTypes.func.isRequired,
};

export default UnlinkSourceStoreModal;
